import { FormControlLabel, Switch, Typography } from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import BudgetControlChart from "./BudgetControlChart";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";
import { setIsBudgetOverused } from "../../../../../features/bizDevelopmentDashboardSlice";

const BudgetControlGraphView = () => {
  const dispatch = useDispatch();
  //is enabled budget overuse toggle
  const budgetOveruse = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isBudgetOverused,
  );
  const [selectedBar, setSelectedBar] = useState<any | null>(null);

  const budgetDataOfBizDevelopment = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboard.budgetDataOfBizDevelopment ?? [],
  );

  const chartData = useMemo(() => {
    if (!budgetDataOfBizDevelopment || budgetDataOfBizDevelopment?.length === 0)
      return [];
    return budgetDataOfBizDevelopment.map((item: any) => ({
      name: item["brand_display_name"],
      value: item["perc_of_budget_used"]
        ? item["perc_of_budget_used"] * 100
        : 0,
    }));
  }, [budgetDataOfBizDevelopment]);

  const handleBarClick = (barData: any) => {
    const matched = budgetDataOfBizDevelopment?.find(
      (item: any) => item["brand_display_name"] === barData.name,
    );
    // toggle off if same bar clicked again
    setSelectedBar((prev: any) =>
      prev?.["brand_display_name"] === barData.name ? null : (matched ?? null),
    );
  };

  const formatMoney = (val: any) =>
    val !== undefined && val !== null ? `${Number(val).toLocaleString()}$` : "";

  const formatPerc = (val: any) =>
    val !== undefined && val !== null
      ? `${parseFloat(Number(val).toFixed(2))}%`
      : "";

  const percBadge = (val: any) => {
    const num = parseFloat(val);
    const isOver = num >= 100;
    return (
      <Box
        sx={{
          backgroundColor: isOver ? "#FDEDED" : "#EDF7ED",
          color: isOver ? "#C62828" : "#2E7D32",
          border: `1px solid ${isOver ? "rgba(198,40,40,0.5)" : "rgba(46,125,50,0.5)"}`,
          borderRadius: "4px",
          px: "8px",
          py: "1px",
          fontSize: "13px",
          fontWeight: 400,
          whiteSpace: "nowrap",
        }}
      >
        {formatPerc(val)}
      </Box>
    );
  };

  // Each row in detail table: [label, value] × 3 columns
  const detailRows = selectedBar
    ? [
        [
          {
            label: "Budget Used",
            value: formatPerc(selectedBar["perc_of_budget_used"] * 100),
          },
          {
            label: "% QFTD Budget Used",
            value: selectedBar["perc_of_qftd_budget_used"]
              ? percBadge(selectedBar["perc_of_qftd_budget_used"] * 100)
              : "",
            isBadge: true,
          },
          {
            label: "CPA Income",
            value: selectedBar["cpa_income"]
              ? formatMoney(selectedBar["cpa_income"])
              : "",
          },
        ],
        [
          {
            label: "Total Budget",
            value: selectedBar["total_income_constraint"]
              ? formatMoney(selectedBar["total_income_constraint"])
              : "",
          },
          {
            label: "RR Budget Used",
            value: selectedBar["perc_of_qftd_RR_budget_use"]
              ? percBadge(selectedBar["perc_of_qftd_RR_budget_use"] * 100)
              : "",
            isBadge: true,
          },
          {
            label: "Total Income",
            value: formatMoney(selectedBar["total_income"]),
          },
        ],
        [
          { label: "QFTD", value: selectedBar["qftd"] ?? "" },
          {
            label: "Remaining QFTD",
            value:
              selectedBar["remaining_qftd"] != null
                ? parseFloat(Number(selectedBar["remaining_qftd"]).toFixed(2))
                : "",
          },
          { label: "FTD", value: selectedBar["ftd"] ?? "" },
        ],
        [
          {
            label: "CPA Budget",
            value: formatMoney(selectedBar["cpa_constraint"]),
          },
          { label: "", value: "" },
          { label: "", value: "" },
        ],
      ]
    : [];

  if (!budgetDataOfBizDevelopment || budgetDataOfBizDevelopment?.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={380}
      >
        <Typography fontSize="14px" color="text.secondary">
          No data available.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Header row */}
      <Box
        px={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pb={2}
      >
        <Typography variant="body2" fontSize="12px" color="text.secondary">
          Click on a Bar to see the full Details, Or switch to Table View
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="14px">Budget Overuse</Typography>
          <FormControlLabel
            control={
              <Switch
                data-tracker="821"
                color="secondary"
                size="medium"
                checked={budgetOveruse}
                onChange={(e) => {
                  dispatch(setIsBudgetOverused(e.target.checked));
                  setSelectedBar(null);
                }}
              />
            }
            label=""
            sx={{ mr: 0 }}
          />
        </Stack>
      </Box>

      {/* Chart */}
      <BudgetControlChart
        data={chartData}
        showOveruseOnly={budgetOveruse}
        onBarClick={handleBarClick}
        activeBar={selectedBar ? selectedBar["brand_display_name"] : null}
      />

      {/* Detail panel — shown on specific chart bar click */}
      <Box sx={{ px: 2, pt: 2, minHeight: 200 }}>
        {selectedBar && (
          <>
            {/* <Box sx={{ px: 2, pt: 2 }}></Box> */}
            <Typography fontSize="18px" fontWeight={500} mb={2}>
              {selectedBar["brand_display_name"]}
            </Typography>

            {detailRows.map((row, rowIdx) => (
              <Grid
                key={rowIdx}
                container
                sx={{
                  borderBottom:
                    rowIdx < detailRows.length - 1
                      ? "1px solid #F0F0F0"
                      : "none",
                  py: 1,
                }}
              >
                {row.map((cell, colIdx) => (
                  <Grid key={colIdx} size={4}>
                    {cell.label ? (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        pr={4}
                      >
                        <Typography
                          fontSize="13px"
                          fontWeight={500}
                          color="text.primary"
                        >
                          {cell.label}
                        </Typography>
                        {(cell as any).isBadge ? (
                          cell.value
                        ) : (
                          <Typography fontSize="13px" color="text.secondary">
                            {cell.value}
                          </Typography>
                        )}
                      </Stack>
                    ) : null}
                  </Grid>
                ))}
              </Grid>
            ))}
          </>
        )}
      </Box>
    </>
  );
};
export default BudgetControlGraphView;
